// TasksPOC.js — Documentation Fonctionnelle : POC « Tâches » (module Environnement).
// Aperçu interactif de l'écran réel de gestion des Tâches, FIDÈLE à sa disposition :
//   - barre d'en-tête dégradée (titre + bascule vue table/grille + Exporter JSON / Rafraîchir / Nouvelle Tâche)
//   - bouton « Créer un TASK_FLOW »
//   - barre de filtres (recherche + Environnement + Fonction + Statut + Type)
//   - TABLE pleine largeur (ID, Nom, Fonction, Type, Nature, Statut, Création, Dernière exec., Actions)
//   - barre de pagination
//   - panneau de DÉTAIL latéral (drawer) ouvert au clic d'une ligne, 4 onglets dans l'ordre réel :
//     Informations / Interactions / Exécution / Input-Output
// Les actions sont SIMULÉES localement (démo, aucun appel réseau).
// Source de vérité : automata-studio-frontend [AUT][04]-Tasks.js + TaskToolbarAndFilters.js
//                    + TasksViews.js + TaskDetailsDrawer.js
//                    + automata_environment Task / TaskExecutionStatus / TaskValidationStatus / TaskController.
import React, { useState } from 'react';
import {
  Briefcase, List as ListIcon, LayoutGrid, Download, RefreshCw, Plus, GitBranch, Search,
  PlayCircle, MoreVertical, X, Info, Terminal, ArrowLeftRight, ChevronDown, Code, CheckCircle,
  Key, Zap, Calendar, User, Clock, FileText, AlertCircle, XCircle, Play, Square, EyeOff,
  UserCheck, Eye, Edit2, Save, Wand2, ArrowUpFromLine, ChevronLeft, ChevronRight, Sparkles,
  Copy, Trash2, ArrowRight, Tag, MessageSquare,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Tabs, Icon, SearchBar, EmptyState } from '../ui';

// --- Référentiels (clés back + libellés FR exacts de l'écran réel) -----------
// TaskExecutionStatus (10 valeurs) — la table réelle affiche la CLÉ brute + une pastille colorée.
const EXEC_STATUS = {
  PENDING:      { label: 'En attente',   color: '#3B82F6', icon: Clock },
  TO_EXECUTE:   { label: 'À exécuter',   color: '#3B82F6', icon: Clock },
  ARBITRATED:   { label: 'Arbitré',      color: '#3B82F6', icon: UserCheck },
  IN_PROGRESS:  { label: 'En cours',     color: '#6366F1', icon: Play },
  SUCCESS:      { label: 'Réussi',       color: '#10B981', icon: CheckCircle },
  COMPLETED:    { label: 'Terminé',      color: '#10B981', icon: CheckCircle },
  FAILURE:      { label: 'Échec',        color: '#EF4444', icon: XCircle },
  BLOCKED:      { label: 'Bloqué',       color: '#EF4444', icon: AlertCircle },
  STOPPED:      { label: 'Arrêté',       color: '#F59E0B', icon: Square },
  NOT_TESTABLE: { label: 'Non testable', color: '#9CA3AF', icon: EyeOff },
};
// TaskValidationStatus (5 valeurs) — cycle de vie fonctionnel de la tâche.
const VALID_STATUS = {
  DRAFT:      { label: 'Brouillon',       color: '#9CA3AF' },
  TO_APPROVE: { label: 'À approuver',     color: '#F59E0B' },
  APPROVED:   { label: 'Approuvé',        color: '#10B981' },
  TO_UPDATE:  { label: 'À mettre à jour', color: '#3B82F6' },
  OBSOLETE:   { label: 'Obsolète',        color: '#EF4444' },
};
// Type technique déduit des interactions (deriveTaskType) — icône + couleur de la pastille (TypeIcon réel).
const AGENT_META = {
  GENERO:   { label: 'Genero',          icon: 'MonitorSmartphone', color: '#8B5CF6' },
  DATABASE: { label: 'Base de données', icon: 'Database',          color: '#F59E0B' },
  REST:     { label: 'REST',            icon: 'Server',            color: '#3B82F6' },
  SOAP:     { label: 'SOAP',            icon: 'Send',              color: '#6366F1' },
  OS:       { label: 'OS',              icon: 'Terminal',          color: '#10B981' },
  WEB:      { label: 'Navigateur',      icon: 'Globe',             color: '#06B6D4' },
};
const agentOf = (k) => AGENT_META[k] || { label: 'Tâche', icon: 'HelpCircle', color: '#9CA3AF' };

// Options de filtre (reprises de TaskToolbarAndFilters.statusOptions / typeOptions).
const STATUS_OPTIONS = [
  ['all', 'Tous'], ['PENDING', 'En attente'], ['IN_PROGRESS', 'En cours'], ['COMPLETED', 'Terminé'],
  ['FAILURE', 'Échec'], ['BLOCKED', 'Bloqué'], ['STOPPED', 'Arrêté'], ['SUCCESS', 'Réussi'],
];
const TYPE_OPTIONS = [
  ['all', 'Tous'], ['ACCEPTABILITY', 'Acceptabilité'], ['CORRECTIVE', 'Corrective'], ['EVOLUTION', 'Évolution'],
  ['NON_REGRESSION', 'Non-régression'], ['END_TO_END', 'Bout en bout'], ['PARTNER', 'Partenaire'], ['UNDEFINED', 'Non défini'],
];

// --- Jeu de données en dur ---------------------------------------------------
const SAMPLE = [
  {
    id: 'TSK-1001', taskName: 'Virement interne — saisie écran', taskDescription: 'Saisie + validation d\'un virement de compte à compte.',
    agentType: 'GENERO', taskType: 'NON_REGRESSION', taskNature: 'FONCTIONNELLE', functionName: 'Virements',
    executionStatus: 'SUCCESS', validationStatus: 'APPROVED', taskPriority: 'HIGH', executionType: 'MANUAL',
    createdDate: 'il y a 6 j', lastExec: 'il y a 2 j', createdBy: 'a.dupont', login: 'AGENT01', hasPassword: true,
    scenario: 'Depuis le menu Virements, saisir le montant puis valider et contrôler le solde affiché.',
    expectedResult: 'Le virement est accepté et le solde mis à jour à l\'écran.', notes: 'Compte de test 00012345.', reserves: '',
    interactions: [
      { sequence: 1, type: 'REC_ACTION', direction: 'REQUEST',  status: 'SUCCESS', label: 'Ouvrir le menu Virements' },
      { sequence: 2, type: 'REC_ACTION', direction: 'REQUEST',  status: 'SUCCESS', label: 'Saisir le montant' },
      { sequence: 3, type: 'REC_STATE',  direction: 'RESPONSE', status: 'SUCCESS', label: 'Contrôle solde affiché', controls: 1 },
    ],
    inputs: [{ name: 'montant', type: 'number', required: true, defaultValue: '150000', variable: false }],
    outputs: [{ name: 'reference', type: 'string', path: 'userInterface.fields.REF' }],
    payload: null,
    executions: [
      { id: 'EX-9007', status: 'SUCCESS', result: 'Réussi', progress: 100, start: '14/06 09:12', end: '14/06 09:12', duration: '4s' },
      { id: 'EX-8810', status: 'FAILURE', result: 'Échec',  progress: 60,  start: '13/06 17:01', end: '13/06 17:01', duration: '2s' },
    ],
  },
  {
    id: 'TSK-1002', taskName: 'Contrôle solde compte (SQL)', taskDescription: 'Vérifie le solde d\'un compte directement en base.',
    agentType: 'DATABASE', taskType: 'ACCEPTABILITY', taskNature: 'MÉTIER', functionName: 'Comptes',
    executionStatus: 'PENDING', validationStatus: 'TO_APPROVE', taskPriority: 'MEDIUM', executionType: 'MANUAL',
    createdDate: 'il y a 5 j', lastExec: null, createdBy: 'm.koffi', login: '', hasPassword: false,
    scenario: 'Exécuter la requête SQL et extraire la colonne SOLDE.',
    expectedResult: 'La requête retourne une ligne avec un solde positif.', notes: '', reserves: 'Base de recette uniquement.',
    interactions: [
      { sequence: 1, type: 'REC_ACTION', direction: 'REQUEST', status: 'PENDING', label: 'Exécuter la requête SQL', controls: 0 },
    ],
    inputs: [{ name: 'numeroCompte', type: 'string', required: true, defaultValue: '', variable: false }],
    outputs: [{ name: 'solde', type: 'number', path: 'database.rows[0].SOLDE' }],
    payload: { kind: 'SQL (base de données)', value: "SELECT solde FROM comptes WHERE numero = '00012345'" },
    executions: [],
  },
  {
    id: 'TSK-1003', taskName: 'Appel API création bénéficiaire', taskDescription: 'POST REST de création d\'un bénéficiaire.',
    agentType: 'REST', taskType: 'END_TO_END', taskNature: 'FONCTIONNELLE', functionName: 'Bénéficiaires',
    executionStatus: 'FAILURE', validationStatus: 'TO_UPDATE', taskPriority: 'HIGH', executionType: 'AUTOMATIC',
    createdDate: 'il y a 4 j', lastExec: 'il y a 1 j', createdBy: 'a.dupont', login: '', hasPassword: false,
    scenario: 'Appeler POST /beneficiaires puis contrôler le code retour 201.',
    expectedResult: 'Code HTTP 201 et identifiant retourné.', notes: 'Jeton d\'auth à variabiliser.', reserves: '',
    interactions: [
      { sequence: 1, type: 'REC_ACTION', direction: 'REQUEST',  status: 'SUCCESS', label: 'POST /beneficiaires' },
      { sequence: 2, type: 'REC_STATE',  direction: 'RESPONSE', status: 'FAILURE', label: 'Contrôle code retour 201', controls: 1 },
    ],
    inputs: [{ name: 'token', type: 'string', required: true, defaultValue: '', variable: true, varName: 'token' }],
    outputs: [{ name: 'idBeneficiaire', type: 'string', path: 'apiResponse.bodyJson.id' }],
    payload: null,
    executions: [
      { id: 'EX-9101', status: 'FAILURE', result: 'Échec', progress: 50, start: '15/06 08:40', end: '15/06 08:40', duration: '1s' },
    ],
  },
  {
    id: 'TSK-1004', taskName: 'Purge fichiers temporaires (Linux)', taskDescription: 'Nettoyage des journaux temporaires de l\'agent.',
    agentType: 'OS', taskType: 'CORRECTIVE', taskNature: 'TECHNIQUE', functionName: 'Exploitation',
    executionStatus: 'NOT_TESTABLE', validationStatus: 'DRAFT', taskPriority: 'LOW', executionType: 'MANUAL',
    createdDate: 'il y a 7 j', lastExec: null, createdBy: 'root', login: '', hasPassword: false,
    scenario: 'Supprimer les fichiers *.log du répertoire temporaire.',
    expectedResult: 'Le répertoire temporaire est vidé.', notes: '', reserves: 'Tâche non rejouable (destructive).',
    interactions: [],
    inputs: [],
    outputs: [],
    payload: { kind: 'Commande OS', value: 'rm -rf /tmp/automata/*.log' },
    executions: [],
  },
];

// --- Sous-composants de la réplique -----------------------------------------
const TypeIcon = ({ agentType, box = 28, size = 16 }) => {
  const a = agentOf(agentType);
  return (
    <span className="inline-flex items-center justify-center rounded-lg flex-shrink-0" title={a.label}
      style={{ width: box, height: box, backgroundColor: `${a.color}1a`, color: a.color }}>
      <Icon name={a.icon} size={size} />
    </span>
  );
};

// StatusBadge fidèle : pastille colorée + CLÉ brute du statut (comme la table réelle).
const StatusBadge = ({ status }) => {
  const s = EXEC_STATUS[status] || EXEC_STATUS.PENDING;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium"
      style={{ backgroundColor: `${s.color}1a`, color: s.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.color }} />
      {status || 'PENDING'}
    </span>
  );
};

const ExecBadge = ({ status }) => {
  const s = EXEC_STATUS[status] || EXEC_STATUS.PENDING;
  const I = s.icon;
  return <Badge color={s.color}><I className="h-3 w-3" />{s.label}</Badge>;
};

const InfoRow = ({ label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700/50 last:border-0">
    <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
    <span className="text-sm font-medium text-gray-900 dark:text-white">{value || '-'}</span>
  </div>
);

const TextSection = ({ label, content, icon: Ic }) => (
  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
    <div className="flex items-center gap-2 mb-2">
      {Ic && <Ic size={16} className="text-gray-400" />}
      <span className="text-sm font-semibold text-gray-900 dark:text-white">{label}</span>
    </div>
    <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">{content || 'Non spécifié'}</p>
  </div>
);

const StatCard = ({ icon: Ic, label, value, color = '#3B82F6' }) => (
  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3">
    <div className="flex items-center gap-2 mb-1">
      <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: color }}>
        <Ic size={14} className="text-white" />
      </span>
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
    </div>
    <p className="text-sm font-medium text-gray-900 dark:text-white pl-9 truncate">{value}</p>
  </div>
);

const GuideStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
  </li>
);

// --- Page --------------------------------------------------------------------
const TasksPOC = () => {
  const [tasks, setTasks] = useState(SAMPLE);
  const [viewMode, setViewMode] = useState('table');          // 'table' | 'grid'
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [drawerId, setDrawerId] = useState(null);             // tâche ouverte dans le drawer
  const [detailTab, setDetailTab] = useState('infos');        // 'infos' | 'interactions' | 'execution' | 'io'
  const [openInteraction, setOpenInteraction] = useState(null);
  const [runState, setRunState] = useState('idle');           // 'idle' | 'running' | 'done'
  const [varModal, setVarModal] = useState(null);             // { taskId, fieldName } | null
  const [varName, setVarName] = useState('');

  const selected = tasks.find((t) => t.id === drawerId) || null;
  const filtered = tasks.filter((t) =>
    (statusFilter === 'all' || t.executionStatus === statusFilter) &&
    (typeFilter === 'all' || t.taskType === typeFilter) &&
    `${t.taskName} ${t.id} ${t.functionName} ${agentOf(t.agentType).label}`.toLowerCase().includes(search.toLowerCase())
  );

  const openDrawer = (id) => { setDrawerId(id); setDetailTab('infos'); setOpenInteraction(null); setRunState('idle'); };
  const closeDrawer = () => setDrawerId(null);

  // Variabilisation simulée : transforme une entrée littérale en ${task.data.<nom>}.
  const openVar = (taskId, fieldName) => { setVarModal({ taskId, fieldName }); setVarName(fieldName); };
  const confirmVar = () => {
    if (!varModal || !varName.trim()) return;
    setTasks((prev) => prev.map((t) => t.id === varModal.taskId
      ? { ...t, inputs: t.inputs.map((f) => f.name === varModal.fieldName ? { ...f, variable: true, varName: varName.trim() } : f) }
      : t));
    setVarModal(null); setVarName('');
  };

  // Rejeu simulé : passe la tâche IN_PROGRESS puis SUCCESS et ajoute une exécution.
  const runTask = (id) => {
    const tid = id || drawerId;
    if (!tid) return;
    setDrawerId(tid); setRunState('running');
    setTasks((prev) => prev.map((t) => t.id === tid ? { ...t, executionStatus: 'IN_PROGRESS' } : t));
    window.setTimeout(() => {
      setTasks((prev) => prev.map((t) => t.id === tid ? {
        ...t, executionStatus: 'SUCCESS', lastExec: 'à l\'instant',
        executions: [{ id: 'EX-NEW', status: 'SUCCESS', result: 'Réussi', progress: 100, start: 'maintenant', end: 'maintenant', duration: '4s' }, ...t.executions],
      } : t));
      setRunState('done'); setDetailTab('execution');
    }, 800);
  };

  return (
    <div className="space-y-8">
      {/* ===== 1. Intro ===== */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Tâches</h1>
          <Badge color="#3B82F6">Module Environnement</Badge>
          <Badge color="#F59E0B">POC interactif</Badge>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Aperçu interactif de l'écran <strong>Gestion des Tâches</strong> (démo, actions simulées localement).
        </p>
      </div>

      {/* ===== 2. Aperçu interactif de l'IHM ===== */}
      <Card className="overflow-hidden">
        <div className="p-4 space-y-4 bg-gray-50 dark:bg-gray-900">

          {/* (a) Barre d'en-tête dégradée */}
          <div className="rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-700 p-5 shadow-lg">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                  <Briefcase size={22} className="text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Gestion des Tâches</h2>
                  <p className="text-blue-100 text-sm">Créer, organiser et exécuter vos tâches de test</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex bg-white/10 rounded-xl p-1">
                  <button onClick={() => setViewMode('table')}
                    className={`p-2 rounded-lg transition-all ${viewMode === 'table' ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white'}`}>
                    <ListIcon size={16} />
                  </button>
                  <button onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white'}`}>
                    <LayoutGrid size={16} />
                  </button>
                </div>
                <button className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors disabled:opacity-40" disabled>
                  <Download size={16} /> Exporter JSON
                </button>
                <button className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors" title="Rafraîchir">
                  <RefreshCw size={18} />
                </button>
                <button className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/20 hover:bg-white/30 text-white text-sm font-medium transition-colors">
                  <Plus size={18} /> Nouvelle Tâche
                </button>
              </div>
            </div>
          </div>

          {/* (b) Bouton Créer un TASK_FLOW */}
          <div>
            <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-600 text-white text-sm hover:bg-sky-700 shadow-sm">
              <GitBranch size={15} /> Créer un TASK_FLOW
            </button>
          </div>

          {/* (c) Barre de filtres */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-2xl p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher..."
                  className="w-full pl-9 pr-4 py-2 rounded-xl bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all" />
              </div>
              <select disabled
                className="min-w-[160px] px-3 py-2 rounded-xl bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-sm text-gray-500 dark:text-gray-400">
                <option>Tous les environnements</option>
              </select>
              <select disabled
                className="min-w-[160px] px-3 py-2 rounded-xl bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-sm text-gray-500 dark:text-gray-400">
                <option>Toutes les fonctions</option>
              </select>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="min-w-[150px] px-3 py-2 rounded-xl bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-sm text-gray-900 dark:text-white">
                {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                className="min-w-[150px] px-3 py-2 rounded-xl bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-sm text-gray-900 dark:text-white">
                {TYPE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>

          {/* (d) TABLE pleine largeur (vue table) ou GRILLE (vue grid) */}
          {viewMode === 'table' ? (
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                {filtered.length === 0 ? (
                  <EmptyState icon={AlertCircle} title="Aucune tâche trouvée" message="Aucune tâche ne correspond aux filtres." />
                ) : (
                  <table className="w-full">
                    <thead className="bg-gray-50/80 dark:bg-gray-900/50">
                      <tr>
                        {['ID', 'Nom', 'Fonction', 'Type', 'Nature', 'Statut', 'Création', 'Dernière exec.'].map((h) => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                        ))}
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                      {filtered.map((t) => (
                        <tr key={t.id} onClick={() => openDrawer(t.id)}
                          className="hover:bg-blue-50/50 dark:hover:bg-blue-900/10 cursor-pointer transition-colors group">
                          <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 font-mono">{t.id}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2.5">
                              <TypeIcon agentType={t.agentType} />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white">{t.taskName}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[220px]">{t.taskDescription}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
                              {t.functionName}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{t.taskType}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{t.taskNature}</td>
                          <td className="px-4 py-3"><StatusBadge status={t.executionStatus} /></td>
                          <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{t.createdDate}</td>
                          <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                            {t.lastExec || <span className="text-gray-400">Jamais</span>}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={(e) => { e.stopPropagation(); runTask(t.id); }} title="Exécuter"
                                className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors">
                                <PlayCircle size={18} />
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); openDrawer(t.id); }} title="Plus d'actions"
                                className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                                <MoreVertical size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.length === 0 && (
                <div className="col-span-full"><EmptyState icon={AlertCircle} title="Aucune tâche trouvée" message="Aucune tâche ne correspond aux filtres." /></div>
              )}
              {filtered.map((t) => (
                <div key={t.id} onClick={() => openDrawer(t.id)}
                  className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden cursor-pointer hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-700 transition-all">
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
                        <TypeIcon agentType={t.agentType} />
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">{t.taskName}</h3>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); openDrawer(t.id); }}
                        className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0">
                        <MoreVertical size={16} />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">{t.taskDescription}</p>
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">{t.functionName}</span>
                      <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">{t.taskType}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <StatusBadge status={t.executionStatus} />
                      <span className="text-xs text-gray-400">{t.createdDate}</span>
                    </div>
                  </div>
                  <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-2 flex items-center justify-between">
                    <span className="text-xs text-gray-400 font-mono">ID: {t.id}</span>
                    <button onClick={(e) => { e.stopPropagation(); runTask(t.id); }} title="Exécuter"
                      className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors">
                      <PlayCircle size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* (e) Barre de pagination */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-2xl p-4 flex items-center justify-between flex-wrap gap-3">
            <span className="text-sm text-gray-500 dark:text-gray-400">{filtered.length} tâches trouvées sur {tasks.length} au total</span>
            <div className="flex items-center gap-3">
              <select disabled className="px-3 py-2 rounded-xl bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-sm text-gray-500 dark:text-gray-400">
                <option>20 / page</option>
              </select>
              <div className="flex items-center gap-1">
                <button disabled className="px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium disabled:opacity-40 flex items-center gap-1">
                  <ChevronLeft size={16} /> Précédent
                </button>
                <button disabled className="px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium disabled:opacity-40 flex items-center gap-1">
                  Suivant <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ===== Panneau de DÉTAIL (drawer) — ouvert au clic d'une ligne ===== */}
        {selected && (
          <div className="border-t-4 border-blue-500 dark:border-blue-600">
            <div className="flex flex-col bg-gray-50 dark:bg-gray-900">
              {/* En-tête du drawer */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-5">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-bold text-white">Détails de la tâche</h2>
                  <button onClick={closeDrawer} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors">
                    <X size={18} />
                  </button>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{selected.taskName}</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium bg-white/20 text-white">
                    {(() => { const I = (EXEC_STATUS[selected.executionStatus] || EXEC_STATUS.PENDING).icon; return <I size={14} />; })()}
                    {selected.executionStatus}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium bg-white/15 text-white/90">
                    <Code size={14} />{selected.functionName}
                  </span>
                  <span className="px-3 py-1 rounded-lg text-xs font-medium bg-white/10 text-white/80">{selected.taskType}</span>
                  <span className="px-3 py-1 rounded-lg text-xs font-medium bg-white/10 text-white/80">{selected.taskPriority}</span>
                </div>
                {/* Onglets — ordre RÉEL : Informations / Interactions / Exécution / Input-Output */}
                <Tabs
                  tabs={[
                    { id: 'infos', label: 'Informations' },
                    { id: 'interactions', label: 'Interactions', count: selected.interactions.length },
                    { id: 'execution', label: 'Exécution', count: selected.executions.length },
                    { id: 'io', label: 'Input / Output', count: selected.inputs.length + selected.outputs.length },
                  ]}
                  active={detailTab} onChange={setDetailTab}
                  className="border-white/20 [&_button]:text-white/70 [&_button]:hover:text-white"
                />
              </div>

              {/* Corps du drawer */}
              <div className="p-6">
                {/* --- Onglet Informations --- */}
                {detailTab === 'infos' && (
                  <div className="space-y-4">
                    <TextSection label="Description" content={selected.taskDescription} icon={FileText} />
                    <TextSection label="Scénario" content={selected.scenario} icon={FileText} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-2"><Key size={16} className="text-gray-400" /> Informations d'accès</h4>
                        <InfoRow label="Login" value={selected.login || 'Non spécifié'} />
                        <InfoRow label="Mot de passe" value={selected.hasPassword ? '••••••••' : 'Non spécifié'} />
                      </div>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-2"><Zap size={16} className="text-gray-400" /> Détails de l'exécution</h4>
                        <InfoRow label="Type" value={selected.executionType} />
                        <InfoRow label="Dernière exec." value={selected.lastExec || 'Jamais'} />
                      </div>
                    </div>
                    <TextSection label="Résultat attendu" content={selected.expectedResult} icon={CheckCircle} />
                    {selected.notes && <TextSection label="Notes" content={selected.notes} icon={MessageSquare} />}
                    {selected.reserves && <TextSection label="Réserves" content={selected.reserves} icon={AlertCircle} />}
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Métadonnées</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <StatCard icon={Zap} label="ID" value={selected.id} color="#3B82F6" />
                        <StatCard icon={User} label="Créé par" value={selected.createdBy} color="#8B5CF6" />
                        <StatCard icon={Calendar} label="Date de création" value={selected.createdDate} color="#10B981" />
                        <StatCard icon={Clock} label="Dernière modification" value={selected.lastExec || 'Aucune'} color="#F59E0B" />
                      </div>
                    </div>
                  </div>
                )}

                {/* --- Onglet Interactions (accordéons) --- */}
                {detailTab === 'interactions' && (
                  <div className="space-y-2">
                    {selected.interactions.length === 0
                      ? <EmptyState icon={Terminal} title="Aucune interaction trouvée" message="Cette tâche n'a pas encore été enregistrée." />
                      : selected.interactions.map((it) => {
                        const on = openInteraction === it.sequence;
                        return (
                          <div key={it.sequence} className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <button onClick={() => setOpenInteraction(on ? null : it.sequence)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                              <span className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-xs font-bold text-blue-600 dark:text-blue-400">{it.sequence}</span>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">{it.type}</span>
                              <span className="px-2 py-0.5 rounded-md text-xs font-medium" style={{ backgroundColor: `${(EXEC_STATUS[it.status] || EXEC_STATUS.PENDING).color}1a`, color: (EXEC_STATUS[it.status] || EXEC_STATUS.PENDING).color }}>{it.status}</span>
                              <span className="text-xs text-gray-400 ml-auto mr-2">{it.direction}</span>
                              <ChevronDown size={16} className={`text-gray-400 transition-transform ${on ? 'rotate-180' : ''}`} />
                            </button>
                            {on && (
                              <div className="px-4 pb-4 space-y-3">
                                <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-3">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Propriétés techniques</span>
                                    <button className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-1.5"><Code size={14} /> Voir JSON</button>
                                  </div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 italic">{it.label}</p>
                                </div>
                                <div>
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <CheckCircle size={16} className="text-gray-400" />
                                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Contrôles de validation ({it.controls || 0})</span>
                                    </div>
                                    {it.type === 'REC_STATE' && it.direction === 'RESPONSE' && (
                                      <button className="px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 flex items-center gap-1.5"><Plus size={14} /> Ajouter</button>
                                    )}
                                  </div>
                                  {it.controls > 0
                                    ? <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 font-mono text-xs text-gray-700 dark:text-gray-300"><strong>IF</strong> technicalProperties.code EQUALS ${'{task.data.codeAttendu}'}</div>
                                    : <p className="text-xs text-gray-400 italic py-2">Aucun contrôle défini pour cette interaction</p>}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}

                {/* --- Onglet Exécution --- */}
                {detailTab === 'execution' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">Historique des exécutions ({selected.executions.length})</h3>
                      <button className="px-3 py-1.5 rounded-xl border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-1.5"><RefreshCw size={14} /> Actualiser</button>
                    </div>
                    {selected.executions.length === 0 ? (
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-8 text-center">
                        <AlertCircle size={48} className="text-gray-300 mx-auto mb-3" />
                        <p className="text-sm text-gray-500 dark:text-gray-400">Aucune exécution disponible</p>
                      </div>
                    ) : (
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-gray-900/50">
                              <tr>{['ID', 'Statut', 'Résultat', 'Progression', 'Début', 'Fin', 'Durée', 'Actions'].map((h) => <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>)}</tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                              {selected.executions.map((e, i) => (
                                <tr key={i} className="hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors">
                                  <td className="px-3 py-2 text-sm font-mono text-gray-500">#{e.id}</td>
                                  <td className="px-3 py-2"><span className="px-2 py-0.5 rounded-md text-xs font-medium" style={{ backgroundColor: `${(EXEC_STATUS[e.status] || EXEC_STATUS.PENDING).color}1a`, color: (EXEC_STATUS[e.status] || EXEC_STATUS.PENDING).color }}>{e.status}</span></td>
                                  <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-200">{e.result}</td>
                                  <td className="px-3 py-2">
                                    <div className="flex items-center gap-2">
                                      <div className="flex-1 h-1.5 w-20 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                        <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full" style={{ width: `${e.progress}%` }} />
                                      </div>
                                      <span className="text-xs text-gray-500 w-8">{e.progress}%</span>
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-500">{e.start}</td>
                                  <td className="px-3 py-2 text-xs text-gray-500">{e.end}</td>
                                  <td className="px-3 py-2 text-xs text-gray-500 font-mono">{e.duration}</td>
                                  <td className="px-3 py-2"><button className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors" title="Voir les détails"><Eye size={16} /></button></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* --- Onglet Input / Output --- */}
                {detailTab === 'io' && (
                  <div className="space-y-4">
                    {/* En-tête contrat I/O */}
                    <div className="flex items-center justify-between bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 flex-wrap gap-3">
                      <div className="flex items-center gap-2">
                        <TypeIcon agentType={selected.agentType} box={32} size={18} />
                        <div>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">Contrat d'entrée / sortie</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Type détecté : {agentOf(selected.agentType).label}</p>
                        </div>
                      </div>
                      <button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-sm font-medium hover:from-blue-600 hover:to-indigo-700 shadow-lg"><Save size={16} /> Enregistrer le contrat</button>
                    </div>
                    {/* Entrée (input) */}
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">Entrée (input)</span>
                        <span className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400"><input type="checkbox" readOnly checked={selected.inputs.length > 0} /> Cette tâche attend une entrée</span>
                      </div>
                      {selected.inputs.length === 0
                        ? <p className="text-sm text-gray-400">Cette tâche n'attend aucune entrée.</p>
                        : <div className="space-y-2">{selected.inputs.map((f) => (
                            <div key={f.name} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">{f.name} <span className="text-xs text-gray-400">({f.type}{f.required ? ', requis' : ''})</span></div>
                                <div className="text-xs font-mono mt-0.5">
                                  {f.variable
                                    ? <span className="text-violet-600 dark:text-violet-400">{'${task.data.' + (f.varName || f.name) + '}'}</span>
                                    : <span className="text-gray-500 dark:text-gray-400">valeur : {f.defaultValue || '—'}</span>}
                                </div>
                              </div>
                              {f.variable
                                ? <Badge color="#8B5CF6"><Wand2 className="h-3 w-3" />Variabilisée</Badge>
                                : <Button variant="outline" size="sm" onClick={() => openVar(selected.id, f.name)}><Wand2 className="h-4 w-4" />Variabiliser</Button>}
                            </div>
                          ))}</div>}
                    </div>
                    {/* Sortie (output) */}
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">Sortie (output)</span>
                        <button className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-emerald-300 dark:border-emerald-700 text-emerald-600 dark:text-emerald-400 text-xs font-medium hover:bg-emerald-50 dark:hover:bg-emerald-900/20"><ArrowUpFromLine size={14} /> Ajouter depuis la dernière exécution</button>
                      </div>
                      {selected.outputs.length === 0
                        ? <p className="text-sm text-gray-400">Cette tâche ne produit aucune sortie.</p>
                        : <div className="space-y-2">{selected.outputs.map((f) => (
                            <div key={f.name} className="p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">{f.name} <span className="text-xs text-gray-400">({f.type})</span></div>
                              <div className="text-xs font-mono text-gray-500 dark:text-gray-400 mt-0.5">extractRaw : {f.path}</div>
                            </div>
                          ))}</div>}
                    </div>
                    {/* Variabilisation des payloads libres (DB / OS / SOAP) */}
                    {selected.payload && (
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-1"><Wand2 size={16} className="text-violet-500" /><span className="text-sm font-semibold text-gray-900 dark:text-white">Variabiliser les payloads (SQL / commande / XML)</span></div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Pour DB / OS / SOAP, sélectionnez la portion à rendre variable dans le payload (pas de détection automatique).</p>
                        <div className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                          <span className="text-[11px] font-semibold text-gray-400 w-8 flex-shrink-0">#1</span>
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-medium text-gray-700 dark:text-gray-300">{selected.payload.kind}</div>
                            <div className="text-[11px] text-gray-400 font-mono truncate" title={selected.payload.value}>{selected.payload.value}</div>
                          </div>
                          <button className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-violet-300 dark:border-violet-700 text-violet-600 dark:text-violet-400 text-xs font-medium hover:bg-violet-50 dark:hover:bg-violet-900/20 flex-shrink-0"><Wand2 size={14} /> Variabiliser</button>
                        </div>
                      </div>
                    )}
                    <p className="text-xs text-gray-400 dark:text-gray-500">Ces champs alimentent le câblage des TASK_FLOW : la sortie d'une étape peut être branchée sur l'entrée de l'étape suivante.</p>
                  </div>
                )}
              </div>

              {/* Pied du drawer (actions) */}
              <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-3 flex items-center justify-end gap-3">
                <Button variant="outline" size="sm"><Edit2 className="h-4 w-4" />Modifier</Button>
                <Button size="sm" onClick={() => runTask(selected.id)} disabled={runState === 'running' || selected.executionStatus === 'NOT_TESTABLE'}>
                  {runState === 'running' ? 'Exécution…' : <><PlayCircle className="h-4 w-4" />Exécuter</>}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Modale de variabilisation simulée */}
      {varModal && (
        <Card className="border-violet-300 dark:border-violet-500/40">
          <CardContent>
            <div className="flex items-center gap-2 mb-3">
              <Wand2 className="h-5 w-5 text-violet-500" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Sélectionner &amp; variabiliser — entrée « {varModal.fieldName} »</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              Nommez la variable : l'entrée littérale sera remplacée par <code className="font-mono text-violet-600 dark:text-violet-400">{'${task.data.<nom>}'}</code>.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[220px]">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input value={varName} onChange={(e) => setVarName(e.target.value)} placeholder="nom de la variable (ex: montant)"
                  className="w-full pl-10 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none" />
              </div>
              <Button onClick={confirmVar} disabled={!varName.trim()}><Plus className="h-4 w-4" />Créer la variable</Button>
              <Button variant="ghost" onClick={() => setVarModal(null)}>Annuler</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ===== 3. Guide utilisateur ===== */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Guide utilisateur</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Eye className="h-5 w-5 text-blue-500" />1. Consulter une tâche</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Filtrer la table par <strong>statut</strong>/<strong>type</strong> ou via la <strong>recherche</strong>.</GuideStep>
              <GuideStep n={2}>Cliquer une ligne pour ouvrir le détail. <button onClick={() => openDrawer('TSK-1001')} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={3}>Parcourir les onglets <strong>Informations</strong>, <strong>Interactions</strong>, <strong>Exécution</strong>.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Wand2 className="h-5 w-5 text-violet-500" />2. Variabiliser une entrée</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Ouvrir l'onglet <strong>Input / Output</strong>. <button onClick={() => { openDrawer('TSK-1001'); setDetailTab('io'); }} className="text-blue-600 dark:text-blue-400 underline">Ouvrir</button></GuideStep>
              <GuideStep n={2}>Cliquer <strong>« Variabiliser »</strong> sur l'entrée souhaitée. <button onClick={() => { openDrawer('TSK-1001'); setDetailTab('io'); openVar('TSK-1001', 'montant'); }} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={3}>Nommer la variable → l'entrée devient <code className="font-mono text-violet-600 dark:text-violet-400">{'${task.data.…}'}</code>, <strong>réutilisable</strong> dans un Task Flow.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><PlayCircle className="h-5 w-5 text-emerald-500" />3. Rejouer une tâche</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Sélectionner une tâche <strong>testable</strong> (statut ≠ Non testable).</GuideStep>
              <GuideStep n={2}>Cliquer <strong>« Exécuter »</strong> (ligne survolée ou pied du détail). <button onClick={() => runTask('TSK-1002')} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={3}>Suivre l'avancement, puis consulter l'onglet <strong>Exécution</strong> (statut, résultat, durée, logs).</GuideStep>
            </ol>
          </CardContent></Card>
        </div>
      </div>

      {/* ===== 4. Référence : statuts, règles, API ===== */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Info className="h-5 w-5 text-blue-500" />Référence des statuts</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Statut d'exécution — <code className="font-mono text-xs">TaskExecutionStatus</code></h4>
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                    <tr>{['Valeur', 'Libellé'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
                    {Object.entries(EXEC_STATUS).map(([k, v]) => (
                      <tr key={k} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-4 py-2 font-mono text-xs">{k}</td>
                        <td className="px-4 py-2"><Badge color={v.color}>{v.label}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Statut de validation — <code className="font-mono text-xs">TaskValidationStatus</code></h4>
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                    <tr>{['Valeur', 'Libellé'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
                    {Object.entries(VALID_STATUS).map(([k, v]) => (
                      <tr key={k} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-4 py-2 font-mono text-xs">{k}</td>
                        <td className="px-4 py-2"><Badge color={v.color}>{v.label}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400 mt-2">Cycle : Brouillon → À approuver → Approuvé (→ À mettre à jour / Obsolète).</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Règles de gestion</h3>
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Une tâche provient d'un <strong>enregistrement (REC)</strong> via l'agent puis est <strong>rejouée (RUN)</strong> automatiquement.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Le type d'agent (Genero, BDD, REST/SOAP, OS, Web) est <strong>déduit des interactions</strong> enregistrées.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Un contrôle de validation ne s'ajoute que sur une interaction <code className="font-mono text-xs">REC_STATE</code> / <code className="font-mono text-xs">RESPONSE</code>.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Une entrée variabilisée s'écrit <code className="font-mono text-violet-600 dark:text-violet-400">{'${task.data.<nom>}'}</code> et devient réutilisable dans un Task Flow.</li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />Une tâche au statut <strong>Non testable</strong> ne peut pas être exécutée.</li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />L'ajout d'une sortie « depuis la dernière exécution » exige <strong>au moins un rejeu</strong> au préalable.</li>
          </ul>
        </CardContent></Card>

        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Endpoints API</h3>
          <ul className="space-y-1.5 text-sm font-mono text-gray-700 dark:text-gray-200">
            {[
              ['GET', '#10B981', '/api/automata/tasks'],
              ['GET', '#10B981', '/api/automata/tasks/page'],
              ['GET', '#10B981', '/api/automata/tasks/{id}'],
              ['POST', '#3B82F6', '/api/automata/tasks'],
              ['PUT', '#F59E0B', '/api/automata/tasks/{id}'],
              ['PATCH', '#8B5CF6', '/api/automata/tasks/{id}/io'],
              ['POST', '#3B82F6', '/api/automata/tasks/{id}/variabilize'],
              ['POST', '#3B82F6', '/api/automata/tasks/{id}/infer-inputs'],
              ['DELETE', '#EF4444', '/api/automata/tasks/{id}'],
            ].map((r, i) => (
              <li key={i} className="flex items-center gap-2"><Badge color={r[1]}>{r[0]}</Badge><span className="break-all">{r[2]}</span></li>
            ))}
          </ul>
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1"><ArrowRight className="h-3 w-3" />Source : automata_environment / TaskController</p>
        </CardContent></Card>
      </div>

      {/* Référence : actions disponibles (menu contextuel de l'écran réel) */}
      <Card><CardContent>
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><FileText className="h-5 w-5 text-blue-500" />Actions disponibles sur une tâche (menu …)</h3>
        <div className="flex flex-wrap gap-2">
          {[
            [Sparkles, 'Générer cas de test', '#8B5CF6'],
            [Eye, 'Voir les détails', '#3B82F6'],
            [PlayCircle, 'Exécuter', '#10B981'],
            [Edit2, 'Modifier', '#F59E0B'],
            [Copy, 'Dupliquer', '#6B7280'],
            [Download, 'Exporter JSON', '#14B8A6'],
            [Trash2, 'Supprimer', '#EF4444'],
          ].map(([Ic, lbl, c], i) => (
            <span key={i} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200">
              <Ic className="h-4 w-4" style={{ color: c }} />{lbl}
            </span>
          ))}
        </div>
      </CardContent></Card>
    </div>
  );
};

export default TasksPOC;
